// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAggregationData } from '@shared/advisory/aggregation/providers';
import { useCallback, useMemo } from 'react';
import { exportTableToExcel, useStateValue } from '@shared/ui-kit';
import { buildAccountBreakdown } from '../../utils/build-account-breakdown';
import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';

export const usePositions = () => {
  const {
    status,
    historicalBalances,
    hasHistoricalBalances,
    periodPicker,
    currency,
    institutionId,
    onRefresh,
    accountsPositions,
    hasHoldings,
  } = useAggregationData();

  const [viewMode, setViewMode] = useStateValue<'market' | 'product-family'>(
    'aggregator-positions-view-mode',
    'market',
  );

  const holdings = useMemo(() => {
    switch (viewMode) {
      case 'market':
        return accountsPositions.assetClassHoldings;
      case 'product-family':
        return accountsPositions.assetTypeHoldings;
    }
  }, [accountsPositions, viewMode]);

  const breakdowns = useMemo(
    () => buildAccountBreakdown(accountsPositions.holdings),
    [accountsPositions.holdings],
  );

  const onDownload = useCallback((data: AccountsHoldingsData) => {
    try {
      exportTableToExcel(
        data.subRows.map(subRow => ({
          'Asset class': subRow.asset_class,
          'Asset type': subRow.asset_type,
          Name: subRow.name,
          Ticker: subRow.ticker,
          ISIN: subRow.isin,
          Currency: subRow.currency,
          Quantity: subRow.quantity,
          Price: subRow.price,
          Investment: subRow.investment,
        })),
        `Investment positions.xlsx`,
      );
    } catch (error) {
      console.error(error);
    }
  }, []);

  return {
    holdings,
    breakdowns,
    hasHoldings,
    status,
    historicalBalances,
    hasHistoricalBalances,
    onDownload,
    periodPicker,
    currency,
    institutionId,
    onRefresh,
    viewMode,
    setViewMode,
  } as const;
};
